/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../supabase';
import { useCanAccessPortal, usePortalPermissions, useCanSeeMailIntel } from '../../hooks/usePortalPermissions';
import { useUiStore, DEFAULT_DEV_VIEW } from '../../stores/uiStore';
import { useProjectsUiStore } from '../../portals/projects/projectsUiStore';
import { useOrdersUiStore } from '../../portals/orders/ordersUiStore';
import { useNotificationsStore } from '../../stores/notificationsStore';
import { PORTAL_NAV, portalKeyFromTab, defaultSub } from '../../constants/portals';
import { enterHome } from './enterHome';
import { PORTAL_TAB_MAP } from '../../portalConfig';
import { useSubsectionCounts } from '../../hooks/useSubsectionCounts';
import { shortcutLabel } from '../../utils/shortcutLabel';
import { ActivityDrawer } from '../system/ActivityDrawer';
import { ErrorBoundary } from '../../ErrorBoundary';
import { Ico } from './ShellIcons';
import { BrandMark } from './BrandMark';
import PortalMegaMenu from './PortalMegaMenu';
import { useMegaHover } from './useMegaHover';
import { useShellStore } from './shellStore';
import { useCanSeeMailPortal } from '../../portals/mail/mailGate';
import sh from './shell.module.css';

// ============================================================
// AppBar — row 1 of Shell v3 (56px): brand · the six portal tabs, each with a
// hover mega-menu · global search · deadlines (clock) · notifications (bell) ·
// menu. Replaces AppHeader under the flag; drives the SAME stores and hooks
// (portal switch resets the Developments sub-state exactly as AppHeader did).
// ⌘K stays App.js's (Convention #169) — this bar adds no second claimant.
// ============================================================

const badgeText = (n, cap) => (!n || n <= 0 ? null : n > cap ? `${cap}+` : String(n));

export default function AppBar({ user, role, tab, setTab, showToast, deadlineCount, notifCount, onToggleDeadlines, deadlinesOpen, onToggleNotifications, notifOpen, setShowGlobalSearch, setGlobalSearch, onBackToPortals, onPrimary }) {
  const [query, setQuery] = useState('');
  const condensed = useShellStore((s) => s.condensed);
  const closeAll = useShellStore((s) => s.closeAll);
  const toggleHamburgerDrawer = useUiStore((s) => s.toggleHamburgerDrawer);
  const activityOpen = useUiStore((s) => s.showActivityDrawer);
  const setActivityOpen = useUiStore((s) => s.setShowActivityDrawer);
  const devView = useUiStore((s) => s.devView);
  const salesView = useUiStore((s) => s.salesView);
  const setDevView = useUiStore((s) => s.setDevView);
  const setAnalyticsView = useUiStore((s) => s.setAnalyticsView);
  const setSettingsView = useUiStore((s) => s.setSettingsView);
  const setSettingsPanel = useUiStore((s) => s.setSettingsPanel);
  const setSalesView = useUiStore((s) => s.setSalesView);
  const projectsView = useProjectsUiStore((s) => s.projectsView);
  const setProjectsView = useProjectsUiStore((s) => s.setProjectsView);
  const ordersWorld = useOrdersUiStore((s) => s.world);
  const ordersView = useOrdersUiStore((s) => s.ordersView);
  const deskView = useOrdersUiStore((s) => s.deskView);
  const collectionView = useOrdersUiStore((s) => s.collection.view);
  const setNotifDrawer = useNotificationsStore((s) => s.setShowDrawer);
  const mega = useMegaHover(); // which portal menu is open — one at a time (CLB-067)

  const openGlobalSearch = (q = '') => { if (typeof setGlobalSearch === 'function') setGlobalSearch(q); setShowGlobalSearch(true); setQuery(''); };

  // 'A' opens the Activity drawer (T-015) — kept from AppHeader.
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'a' && e.key !== 'A') return;
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
      if (activityOpen) return;
      setActivityOpen(true);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activityOpen, setActivityOpen]);

  const activePortalKey = portalKeyFromTab(tab);
  const access = {
    catalog: useCanAccessPortal('catalog'), developments: useCanAccessPortal('developments'), qc: useCanAccessPortal('qc'),
    sales: useCanAccessPortal('sales'), orders: useCanAccessPortal('orders'), projects: useCanAccessPortal('projects'),
    mail: useCanSeeMailPortal(), // MAIL-1: super admin + mi_access only, never useCanAccessPortal
  };
  const { data: ppData } = usePortalPermissions();
  const isAdmin = role === 'admin' || !!ppData?.isSuperAdmin;
  const canSeeMailIntel = useCanSeeMailIntel(role);
  const counts = useSubsectionCounts(true);

  const queryClient = useQueryClient();
  const prefetchPortal = (portalId) => {
    const chunks = {
      qc: () => import(/* webpackChunkName: "portal-qc" */ '../../portals/qc/QcPortal'),
      sales_portal: () => import(/* webpackChunkName: "portal-sales" */ '../../portals/sales/SalesPortal'),
      developments: () => import(/* webpackChunkName: "portal-developments" */ '../../DevelopmentsTab'),
    };
    chunks[portalId]?.();
    if (portalId === 'qc') {
      queryClient.prefetchQuery({ queryKey: ['qc-reports'], staleTime: 30_000, queryFn: async () => {
        const { data, error } = await supabase.from('qc_reports').select('id,report_id,factory,supplier,model,pi,inspector,contact,status,verdict,rounds,round_number,report_group_id,created_at,inspection_date,delivery_status,delivery_original,delivery_actual,locked_by,note_text').order('created_at', { ascending: false });
        if (error) throw error; return data || [];
      } });
    }
  };

  // T-1001 Strategy α: a portal switch resets the Developments sub-state.
  const handlePortalSwitch = (target) => {
    closeAll();
    if (target === 'projects') { useProjectsUiStore.getState().openChooser(); return; }
    setDevView(DEFAULT_DEV_VIEW); setAnalyticsView(null); setSettingsView(null); setSettingsPanel(null); setProjectsView('selection');
    setTab(target);
  };
  const navTab = (p) => PORTAL_TAB_MAP[p.key] || p.key;
  // `whole` = the person clicked the portal itself (its name or the mega menu's Open), not one of its sections
  const handleSection = (portal, sub, whole = false) => {
    if (!sub) { handlePortalSwitch(navTab(portal)); enterHome(portal.key, { setDevView, setSalesView, setTab }); return; }
    handlePortalSwitch(sub.tab);
    if (sub.devView) setDevView(sub.devView);
    if (sub.projectsView) setProjectsView(sub.projectsView);
    if (sub.salesView) setSalesView(sub.salesView);
    if (whole) enterHome(portal.key, { setDevView, setSalesView, setTab });
  };
  const handleOrders = (world, section) => {
    const st = useOrdersUiStore.getState();
    st.setWorld(world);
    if (world === 'book') st.setOrdersView(section);
    else if (world === 'desk') st.setDeskView(section);
    else st.setCollection({ view: section });
    try { localStorage.setItem('clb.orders.world', world); } catch (e) {}
    handlePortalSwitch('orders');
  };
  const ordersSection = ordersWorld === 'book' ? ordersView : ordersWorld === 'desk' ? deskView : collectionView;
  const deadBadge = badgeText(deadlineCount, 99);
  const notifBadge = badgeText(notifCount, 9);

  return (
    <header className={sh.appBar} data-shell-appbar role="banner">
      <button type="button" className={sh.brand} onClick={onBackToPortals} aria-label="Home" title="Home">
        <BrandMark className={sh.mark} /><span className={sh.pipe} /><span className={sh.hub}>Operations Hub</span>
      </button>
      <nav className={sh.nav} aria-label="Portals">
        {PORTAL_NAV.filter((p) => !p.headerHidden && access[p.key]).map((portal) => {
          const isActive = activePortalKey === portal.key;
          const isOpen = mega.openKey === portal.key;
          return (
            <div key={portal.key} className={`${sh.navItem} ${isOpen ? sh.show : ''}`} onMouseEnter={() => prefetchPortal(portal.key === 'sales' ? 'sales_portal' : portal.key)} {...mega.itemProps(portal.key)}>
              <button type="button" className={`${sh.navTab} ${isActive ? sh.active : ''}`} aria-current={isActive ? 'page' : undefined} aria-haspopup="menu" aria-expanded={isOpen} data-test={`shell-portal-${portal.key}`}
                onClick={() => (portal.key === 'orders' || portal.key === 'projects') ? handlePortalSwitch(navTab(portal)) : handleSection(portal, defaultSub(portal), true)}>
                {portal.label}<Ico name="chev" className={sh.chev} />
              </button>
              <PortalMegaMenu
                portal={portal} isActive={isActive} activeTab={tab} devView={devView} salesView={salesView} projectsView={projectsView}
                ordersWorld={ordersWorld} ordersSection={ordersSection} counts={counts}
                onOpen={() => (portal.key === 'orders' || portal.key === 'projects') ? handlePortalSwitch(navTab(portal)) : handleSection(portal, defaultSub(portal), true)}
                onSection={(sub) => handleSection(portal, sub)} onOrders={handleOrders}
                onPrimary={onPrimary ? (action) => { closeAll(); onPrimary(action, portal.key); } : null}
                isAdmin={isAdmin} canSeeMailIntel={canSeeMailIntel} status={portal.key === 'mail' ? { text: 'Read-only' } : { dot: 'live', text: 'Live' }}
              />
            </div>
          );
        })}
      </nav>
      <div className={sh.right}>
        <div className={sh.search}>
          <Ico name="search" className={sh.ico} />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search everything…" aria-label="Global search"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); openGlobalSearch(query); } else if (e.key === 'Escape') { setQuery(''); e.target.blur(); } }} />
          <button type="button" className={`${sh.kbd} ${sh.kbdBtn}`} onClick={() => openGlobalSearch(query)} aria-label="Open global search" title={`Open global search (${shortcutLabel('K')})`}>{shortcutLabel('K')}</button>
        </div>
        <button type="button" className={`${sh.iconBtn} ${deadlinesOpen ? sh.on : ''}`} onClick={(e) => { e.stopPropagation(); onToggleDeadlines(); }} aria-label="Deadlines" data-test="header-deadline-bell" title={deadlineCount > 0 ? `Deadlines · ${deadlineCount} overdue` : 'Deadlines'}>
          <Ico name="clock" />{deadBadge && <span className={`${sh.badge} ${sh.red}`}>{deadBadge}</span>}
        </button>
        <button type="button" className={`${sh.iconBtn} ${notifOpen ? sh.on : ''}`} onClick={(e) => { e.stopPropagation(); if (onToggleNotifications) onToggleNotifications(); else { closeAll(); setNotifDrawer(true); } }} aria-label="Notifications" data-test="header-notifications" title={notifCount > 0 ? `Notifications · ${notifCount} unread` : 'Notifications'}>
          <Ico name="bell" />{notifBadge && <span className={sh.badge} data-test="header-notifications-badge">{notifBadge}</span>}
        </button>
        <button type="button" className={`${sh.iconBtn} ${sh.menuBtn}`} onClick={() => { closeAll(); toggleHamburgerDrawer(); }} aria-label="Open navigation drawer" title="Menu"><Ico name="menu" /></button>
      </div>
      <ErrorBoundary>
        <ActivityDrawer open={activityOpen} onClose={() => setActivityOpen(false)} onOpenTimeline={() => setTab('timeline')} />
      </ErrorBoundary>
    </header>
  );
}
